import { useEffect, useState, useCallback, useMemo } from "react";
import api, { apiErr } from "@/lib/api";
import { METHOD } from "@/constants/testIds";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { fmtDateTime } from "@/lib/format";
import { SlidersHorizontal, History, RotateCcw, ShieldCheck, AlertTriangle, Lock } from "lucide-react";
import { toast } from "sonner";

const PILLARS = ["environmental", "social", "economic"];

export default function MethodCalibratePage() {
  const [profile, setProfile] = useState(null);
  const [kpis, setKpis] = useState([]);
  const [budgets, setBudgets] = useState({});
  const [versions, setVersions] = useState([]);
  const [notes, setNotes] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  const hydrate = (p) => {
    setProfile(p);
    setKpis(p.kpis.map((k) => ({ ...k })));
    setBudgets({ ...p.pillar_budgets });
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, v] = await Promise.all([api.get("/score/profile"), api.get("/score/profile/versions")]);
      hydrate(p.data);
      setVersions(v.data);
    } catch (e) { toast.error(apiErr(e)); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const budgetSum = useMemo(() => Object.values(budgets).reduce((a, b) => a + (Number(b) || 0), 0), [budgets]);

  const errors = useMemo(() => {
    const e = [];
    if (Math.abs(budgetSum - 100) > 1e-6) e.push(`Pillar budgets must sum to 100 (now ${budgetSum}).`);
    for (const k of kpis) {
      if (!(Number(k.base) > 0)) e.push(`${k.code}: base weight must be > 0.`);
      if (k.transform !== "proportion" && Number(k.anchor0) === Number(k.anchor100)) e.push(`${k.code}: anchors must differ.`);
    }
    return e;
  }, [kpis, budgetSum]);

  const setKpi = (code, field, val) => setKpis((rows) => rows.map((k) => k.code === code ? { ...k, [field]: val } : k));

  const resetBaseline = async () => {
    try { const r = await api.get("/score/profile/default"); hydrate(r.data); toast.message("Baseline loaded — review and publish to apply"); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const publish = async () => {
    try {
      const body = {
        pillar_budgets: Object.fromEntries(Object.entries(budgets).map(([k, v]) => [k, Number(v)])),
        kpis: kpis.map((k) => ({ code: k.code, base: Number(k.base),
          anchor0: k.transform === "proportion" ? null : Number(k.anchor0),
          anchor100: k.transform === "proportion" ? null : Number(k.anchor100) })),
        notes,
      };
      const r = await api.post("/score/profile/calibrate", body);
      toast.success(`Published ${r.data.profile_version}`);
      setConfirmOpen(false); setNotes("");
      await load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  if (loading && !profile) return <p className="text-sm text-muted-foreground">Loading scoring method…</p>;

  return (
    <div className="space-y-5" data-testid={METHOD.root}>
      <div>
        <h1 className="flex items-center gap-2 font-heading text-2xl font-bold text-forest-dark"><SlidersHorizontal className="h-6 w-6 text-forest-mid" /> Scoring Method</h1>
        <p className="text-sm text-muted-foreground">Govern the SG Maintenance profile · current version <span className="font-mono">{profile?.profile_version}</span> (method {profile?.method_version})</p>
      </div>

      <div className="flex items-start gap-2 rounded-lg border-l-4 border-forest-mid bg-sage-surface p-3">
        <Lock className="mt-0.5 h-4 w-4 shrink-0 text-forest-mid" />
        <p className="text-xs text-forest-dark">Publishing creates a <b>new, immutable profile version</b>. Already-issued scores stay frozen on their original version; only <b>new draft</b> assessments use the latest version. The KPI set, codes, directions and mandatory flags are fixed — you tune base weights, anchors and pillar budgets.</p>
      </div>

      {/* pillar budgets */}
      <Card className="border-sage-light p-4">
        <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Pillar budgets (must total 100)</p>
        <div className="flex flex-wrap gap-4">
          {PILLARS.map((p) => (
            <div key={p} className="space-y-1.5">
              <Label className="capitalize">{profile?.pillar_labels?.[p] || p}</Label>
              <Input type="number" className="w-28" value={budgets[p] ?? ""} onChange={(e) => setBudgets((b) => ({ ...b, [p]: e.target.value }))} data-testid={METHOD.budget(p)} />
            </div>
          ))}
          <div className="flex items-end">
            <span className={`rounded-full border px-3 py-1 text-sm font-semibold ${Math.abs(budgetSum - 100) < 1e-6 ? "border-emerald-300 bg-emerald-50 text-emerald-800" : "border-rose-300 bg-rose-50 text-rose-700"}`}>Σ {budgetSum}</span>
          </div>
        </div>
      </Card>

      {/* KPI editor */}
      <Card className="border-sage-light p-0">
        <div className="border-b border-sage-light p-4"><h3 className="font-heading text-base font-semibold text-forest-dark">KPI weights & anchors</h3></div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader><TableRow><TableHead>KPI</TableHead><TableHead>Pillar</TableHead><TableHead>Direction</TableHead><TableHead>Base weight</TableHead><TableHead>Anchor → 0</TableHead><TableHead>Anchor → 100</TableHead></TableRow></TableHeader>
            <TableBody>
              {kpis.map((k) => (
                <TableRow key={k.code}>
                  <TableCell className="text-sm"><span className="font-mono text-xs text-muted-foreground">{k.code}</span> {k.name}{k.mandatory && <span className="ml-1 rounded bg-rose-100 px-1 text-[9px] font-semibold text-rose-700">MANDATORY</span>}</TableCell>
                  <TableCell className="text-xs capitalize text-muted-foreground">{k.pillar}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{k.transform === "proportion" ? "proportion" : k.transform === "higher" ? "higher ↑" : "lower ↓"}</TableCell>
                  <TableCell><Input type="number" className="h-8 w-20" value={k.base} onChange={(e) => setKpi(k.code, "base", e.target.value)} data-testid={METHOD.base(k.code)} /></TableCell>
                  <TableCell>{k.transform === "proportion" ? <span className="text-xs text-muted-foreground">—</span> : <Input type="number" className="h-8 w-24" value={k.anchor0 ?? ""} onChange={(e) => setKpi(k.code, "anchor0", e.target.value)} data-testid={METHOD.anchor0(k.code)} />}</TableCell>
                  <TableCell>{k.transform === "proportion" ? <span className="text-xs text-muted-foreground">passed ÷ required</span> : <Input type="number" className="h-8 w-24" value={k.anchor100 ?? ""} onChange={(e) => setKpi(k.code, "anchor100", e.target.value)} data-testid={METHOD.anchor100(k.code)} />}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>

      {errors.length > 0 && (
        <Card className="border-amber-200 bg-amber-50/60 p-3">
          <p className="flex items-center gap-1 text-xs font-semibold text-amber-900"><AlertTriangle className="h-3.5 w-3.5" /> Fix before publishing:</p>
          <ul className="mt-1 list-inside list-disc text-[11px] text-amber-900">{errors.slice(0, 6).map((e, i) => <li key={i}>{e}</li>)}</ul>
        </Card>
      )}

      <div className="flex flex-wrap items-center justify-between gap-3">
        <Button variant="outline" onClick={resetBaseline} data-testid={METHOD.resetBtn}><RotateCcw className="mr-1 h-4 w-4" /> Load built-in baseline</Button>
        <Button disabled={errors.length > 0} onClick={() => setConfirmOpen(true)} data-testid={METHOD.publishBtn} className="bg-forest-mid hover:bg-forest-dark"><ShieldCheck className="mr-1 h-4 w-4" /> Publish new version</Button>
      </div>

      {/* version history */}
      <Card className="border-sage-light p-4">
        <p className="mb-2 flex items-center gap-1.5 font-heading text-base font-semibold text-forest-dark"><History className="h-4 w-4 text-forest-mid" /> Version history</p>
        <div className="space-y-1.5">
          {versions.map((v, i) => (
            <div key={i} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-sage-light p-2 text-xs">
              <span className="font-mono font-semibold text-forest-dark">{v.version} {v.is_current && <span className="ml-1 rounded-full bg-forest-mid px-1.5 py-0.5 text-[9px] text-white">CURRENT</span>}</span>
              <span className="text-muted-foreground">{v.status} · {v.created_by_name}{v.created_at ? ` · ${fmtDateTime(v.created_at)}` : ""}{v.notes ? ` · “${v.notes}”` : ""}</span>
            </div>
          ))}
        </div>
      </Card>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading">Publish a new scoring version</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">This supersedes <span className="font-mono">{profile?.profile_version}</span>. Issued scores remain frozen; new draft assessments will use the new version.</p>
          <div className="space-y-1.5"><Label>Change notes (recommended)</Label><Textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="What changed and why" data-testid={METHOD.notes} /></div>
          <DialogFooter><Button variant="outline" onClick={() => setConfirmOpen(false)}>Cancel</Button><Button onClick={publish} className="bg-forest-mid hover:bg-forest-dark" data-testid={METHOD.confirmPublish}>Publish version</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
